/* @jsx h */
import {
  defineComponent,
  ref,
  provide,
  inject,
  h,
  Ref,
  watch,
  onMounted,
} from "vue";
import { CheckoutComponent } from "@app/vue/components/index";
import { CheckoutData } from "@lemmonhq/types";
import { nanoid } from "nanoid";
import { useShop } from "./ShopConnection";

type CheckoutCallbacks = {
  onSuccess?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
};

type CheckoutContextType = {
  setCheckout: (checkoutData: CheckoutData | null) => void;
  show: (callbacks: CheckoutCallbacks) => void;
  checkout: Ref<CheckoutData | null>;
  hide: Function;
};

const CheckoutContextKey = Symbol("CheckoutContext");

const CHECKOUT_STORAGE_KEY = "lemmon_checkout_data";

// Helper functions for localStorage operations
const saveCheckoutToStorage = (checkoutData: CheckoutData | null) => {
  try {
    if (checkoutData) {
      localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(checkoutData));
    } else {
      localStorage.removeItem(CHECKOUT_STORAGE_KEY);
    }
  } catch (error) {
    console.error("Failed to save checkout to localStorage:", error);
  }
};

const loadCheckoutFromStorage = (): CheckoutData | null => {
  try {
    const stored = localStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as CheckoutData;
    }
  } catch (error) {
    console.error("Failed to load checkout from localStorage:", error);
  }
  return null;
};

export const CheckoutProvider = defineComponent({
  name: "CheckoutProvider",
  setup(_, { slots }) {
    const { booted } = useShop();
    const checkout = ref<CheckoutData | null>(null);
    const callbacks = ref<CheckoutCallbacks>({});
    const checkoutId = ref<string>(nanoid());

    // Restore checkout data from localStorage on mount
    onMounted(() => {
      const storedCheckout = loadCheckoutFromStorage();
      if (storedCheckout) {
        checkout.value = storedCheckout;
      }
    });

    // Watch checkout changes and persist to localStorage
    watch(
      checkout,
      (newCheckout) => {
        saveCheckoutToStorage(newCheckout);
      },
      { deep: true },
    );

    // Awaited so a callback's own async cleanup (e.g. TablePayment's
    // post-checkout refresh) finishes — and can still read `checkout.value`
    // for identifiers it needs — before this clears it. An un-awaited
    // fire-and-forget call previously cleared checkout state synchronously
    // right after invoking the callback, so a callback that failed mid-`await`
    // found its own payment/checkout identifiers already gone, and any
    // rejection became an unhandled promise rejection.
    const handleSuccess = async () => {
      // Once per show(): the success screen can report success twice (its
      // Close button and its auto-close timer), and a caller's callback may
      // do real work (e.g. send the table's order after a credit top-up).
      const onSuccess = callbacks.value.onSuccess;
      callbacks.value = { ...callbacks.value, onSuccess: undefined };
      try {
        await onSuccess?.();
      } catch (error) {
        console.error("checkout onSuccess callback failed", error);
      } finally {
        checkout.value = null;
      }
    };

    const handleCancel = async () => {
      try {
        await callbacks.value.onCancel?.();
      } catch (error) {
        console.error("checkout onCancel callback failed", error);
      } finally {
        checkout.value = null;
      }
    };

    const context: CheckoutContextType = {
      setCheckout: (checkoutData) => {
        checkout.value = checkoutData;
      },
      show: (newCallbacks) => {
        callbacks.value = newCallbacks;
        checkoutId.value = nanoid();
      },
      hide: () => {
        checkout.value = null;
      },
      checkout: checkout,
    };

    provide(CheckoutContextKey, context);

    return () => (
      <>
        {slots.default?.()}
        {checkout.value && booted.value && (
          <CheckoutComponent
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            checkoutId={checkoutId.value}
          />
        )}
      </>
    );
  },
});

export function useCheckout(): CheckoutContextType {
  const context = inject<CheckoutContextType>(CheckoutContextKey);
  if (!context) {
    throw new Error("useCheckout must be used within a CheckoutProvider");
  }
  return context;
}
